import '../styles/TestDetails.scss';

import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { CodeBlock } from '@whistler-ui/k2/components/CodeBlock';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useThemeStore } from '../../../store/theme';
import { componentName } from '../constants';

interface Props {
  assertions: string;
  status: string;
}

export const AssertionList = ({ assertions, status }: Props) => {
  const { t } = useTranslation();
  const { colors } = useThemeStore();
  const parsed = {
    ...JSON.parse(assertions),
    result: status === 'Success' ? 'OK' : 'Fail',
  };
  const [selectedId, setSelectedId] = useState(null);
  const c = useClassHelper(componentName);

  return (
    <ul className={c('__assertion-list')} data-cy="assertion-list">
      {Object.keys(parsed).map((key, index) => {
        const title = key
          .replace(/(assert_|_)/g, ' ')
          .replace('eap aka', 'EAP-AKA')
          .replace('jwt', 'JWT')
          .replace(
            'default smdp use case used and no activation qr code returned',
            'Default SM-DP+ Use Case Used And No Activation (QR) Code Returned',
          );

        const isOpen = selectedId === index;

        if (parsed[key] === 'OK') {
          return (
            <li key={key} data-cy={`assertion-success-${index}`}>
              <Stack direction="row" align="center" gap={2}>
                <div className={c('__icon')}>
                  <CheckCircle size={20} color={colors.success} />
                </div>
                <Paragraph>{title}</Paragraph>
              </Stack>
            </li>
          );
        } else {
          return (
            <li
              key={key}
              className={c('__error')}
              data-cy={`assertion-error-${index}`}
            >
              <button
                type="button"
                className={c('__error--title')}
                onClick={() => {
                  setSelectedId(isOpen ? null : index);
                }}
                aria-expanded={isOpen ? 'true' : 'false'}
                id={`assertion-${index}`}
                aria-controls={`assertion-${index}`}
                data-cy={`assertion-error-${index}`}
              >
                <div className={c('__icon')}>
                  <WarningCircle size={20} color={colors.error} />
                </div>
                <Paragraph>{title}</Paragraph>
              </button>
              <motion.div
                role="region"
                aria-labelledby={`assertion-${index}`}
                className={c('__error--details')}
                initial={{ height: 0, opacity: 0 }}
                animate={
                  isOpen
                    ? { height: 'auto', opacity: 1 }
                    : { height: 0, opacity: 0 }
                }
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
              >
                <CodeBlock
                  testId="n-json-response"
                  className={c('__json-code')}
                  obj={t(parsed[key])}
                />
              </motion.div>
            </li>
          );
        }
      })}
    </ul>
  );
};
